import React, { useMemo, useState } from "react";
import { SearchSelect, type SearchSelectItem } from "@musa.tech/design-system";

const DEFAULT_LABEL_MAX_LENGTH = 60;

/**
 * SearchSelect do DS não corta nome longo na lista de opções (só no valor
 * selecionado, via line-clamp) — nomes de resíduo têm nome bem grande
 * (ex. "Absorventes, materiais filtrantes, panos de limpeza contaminados"),
 * igual ao filtro de material da Pré-seleção (PreSelectionFilters.tsx), que
 * corta com truncate. Corta só o texto exibido; a busca abaixo continua
 * comparando o nome completo, senão um termo que só aparece na parte cortada
 * nunca daria match.
 */
const truncateLabel = (label: string, maxLength: number): string =>
  label.length > maxLength ? `${label.slice(0, maxLength).trimEnd()}…` : label;

interface SearchableSelectFieldProps {
  items: SearchSelectItem[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  labelMaxLength?: number;
}

/**
 * SearchSelect do DS usa shouldFilter:false internamente — espera busca
 * assíncrona via onSearch, não filtra a lista sozinho. Aqui filtramos os
 * items localmente pelo texto digitado, e cobrimos a falta de prop
 * `disabled` no componente com o wrapper documentado no musa-minds.
 */
export const SearchableSelectField: React.FC<SearchableSelectFieldProps> = ({
  items,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  labelMaxLength = DEFAULT_LABEL_MAX_LENGTH,
}) => {
  const [search, setSearch] = useState("");
  const filteredItems = useMemo(() => {
    const matches = search.trim()
      ? items.filter((item) =>
          item.label.toLowerCase().includes(search.trim().toLowerCase()),
        )
      : items;
    return matches.map((item) => ({
      ...item,
      label: truncateLabel(item.label, labelMaxLength),
    }));
  }, [items, search, labelMaxLength]);

  // Ao selecionar, o SearchSelect do DS limpa o texto do próprio input
  // internamente, mas não avisa via onSearch — sem isto o filtro local
  // ficava "travado" no último termo digitado na próxima vez que abrisse.
  const handleChange = (nextValue: string) => {
    setSearch("");
    onChange(nextValue);
  };

  const select = (
    <SearchSelect
      items={filteredItems}
      value={value}
      onChange={handleChange}
      onSearch={setSearch}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText={emptyText}
    />
  );

  if (disabled) {
    return <div className="pointer-events-none opacity-50">{select}</div>;
  }
  return select;
};
